import React, { useState } from "react";
import { Tooltip, Row, Col, Alert, Space, message } from "antd";
import { CalculatorOutlined } from "@ant-design/icons";
import { ModalItem, InputItem, DatePickerItem, ButtonItem } from "../../../Components/Items";
import { Campo, ClaveSeparada } from "./CalculadoraCurp";
import { fechaDeCurp } from "./curp";
import { calcularRfc, mismoRfcBase, SEGMENTOS_RFC } from "./rfc";

// Calculadora de RFC: va como `suffix` del InputItem del RFC en Agregar Empleado, Editar Empleado
// y el paso 1 del alta por pasos, igual que la de CURP (CalculadoraCurp.js). Abre con los datos del
// empleado que ya están en el formulario; si no hay fecha de nacimiento se propone la de la CURP
// capturada. "Usar este RFC" devuelve al formulario el RFC y los datos con que se calculó.
//
// Props:
//   obtenerForm()          el formRef.current del Form de la casa.
//   datosExtra             fechaNacimiento cuando el formulario no la tiene (paso 1 del alta por
//                          pasos). Manda sobre lo del formulario.
//   nacimientoEnFormulario false para no escribir la fecha en el formulario (porque no tiene ese
//                          campo) y sólo entregarla en onUsar.
//   onUsar(datos)          { rfc, fechaNacimiento }.
//
// Los campos del modal son controlados, sin Form anidado, por lo mismo que en la de CURP.

const CAMPOS_NOMBRE = ["nombre", "apellidoPaterno", "apellidoMaterno"];
const VACIO = { nombre: "", apellidoPaterno: "", apellidoMaterno: "", fechaNacimiento: null };

const CalculadoraRfc = ({ obtenerForm, datosExtra, nacimientoEnFormulario = true, onUsar }) => {
  const [visible, setVisible] = useState(false);
  const [datos, setDatos] = useState(VACIO);
  const [rfcCapturado, setRfcCapturado] = useState("");
  const [fechaDeLaCurp, setFechaDeLaCurp] = useState(false);

  const cambiarTexto = (campo) => (e) => setDatos((d) => ({ ...d, [campo]: e.target.value }));
  const cambiarFecha = (fecha) => {
    setDatos((d) => ({ ...d, fechaNacimiento: fecha }));
    setFechaDeLaCurp(false);
  };

  const abrir = () => {
    const form = obtenerForm?.();
    const extra = datosExtra || {};
    const capturada = (extra.fechaNacimiento != null ? extra.fechaNacimiento : form?.getFieldValue("fechaNacimiento")) || null;
    const deCurp = capturada ? null : fechaDeCurp(form?.getFieldValue("curp"));

    setDatos({
      nombre: form?.getFieldValue("nombre") || "",
      apellidoPaterno: form?.getFieldValue("apellidoPaterno") || "",
      apellidoMaterno: form?.getFieldValue("apellidoMaterno") || "",
      fechaNacimiento: capturada || deCurp,
    });
    setFechaDeLaCurp(!!deCurp);
    setRfcCapturado(String(form?.getFieldValue("rfc") || "").trim().toUpperCase());
    setVisible(true);
  };

  const { rfc, faltan } = calcularRfc(datos);
  // Si el RFC capturado es de los mismos datos se conserva: trae la homoclave que dio el SAT.
  const coincide = !!rfc && mismoRfcBase(rfc, rfcCapturado);
  const rfcFinal = coincide ? rfcCapturado : rfc;

  const usar = () => {
    if (!rfcFinal) {
      message.warning(`Falta: ${faltan.join(", ")}`);
      return;
    }
    const form = obtenerForm?.();
    const nombres = {};
    for (const campo of CAMPOS_NOMBRE) {
      if (String(datos[campo] || "").trim() !== String(form?.getFieldValue(campo) || "").trim()) nombres[campo] = datos[campo];
    }
    const nacimiento = nacimientoEnFormulario ? { fechaNacimiento: datos.fechaNacimiento } : {};
    form?.setFieldsValue({ rfc: rfcFinal, ...nombres, ...nacimiento });
    // setFieldsValue no revalida: sin esto se quedaría a la vista un error viejo del RFC.
    Promise.resolve(form?.validateFields(["rfc", ...Object.keys(nombres)])).catch(() => {});
    onUsar?.({ rfc: rfcFinal, fechaNacimiento: datos.fechaNacimiento });
    setVisible(false);
  };

  return (
    <>
      <Tooltip title="Calcular RFC">
        <CalculatorOutlined onClick={abrir} style={{ color: "#00BFD6", cursor: "pointer" }} />
      </Tooltip>
      <ModalItem
        title="Calculadora de RFC"
        visible={visible}
        onCancel={() => setVisible(false)}
        width={640}
        destroyOnClose
        paddingTop
      >
        <Row gutter={16}>
          <Col span={12}>
            <Campo etiqueta="Nombre(s)">
              <InputItem value={datos.nombre} onChange={cambiarTexto("nombre")} placeholder="Nombre(s)" />
            </Campo>
            <Campo etiqueta="Apellido paterno">
              <InputItem value={datos.apellidoPaterno} onChange={cambiarTexto("apellidoPaterno")} placeholder="Apellido paterno" />
            </Campo>
            <Campo etiqueta="Apellido materno">
              <InputItem value={datos.apellidoMaterno} onChange={cambiarTexto("apellidoMaterno")} placeholder="Sin apellido materno" />
            </Campo>
          </Col>
          <Col span={12}>
            <Campo etiqueta="Fecha de nacimiento" ayuda={fechaDeLaCurp ? "Tomada de la CURP capturada." : null}>
              <DatePickerItem value={datos.fechaNacimiento} onChange={cambiarFecha} format="DD-MM-YYYY"
                placeholder="Fecha de nacimiento" style={{ width: "100%" }} />
            </Campo>
          </Col>
        </Row>

        <div style={{
          background: "#f4fcfd", border: "1px solid #b8eaf1", borderRadius: 8,
          padding: "14px 16px", margin: "4px 0 12px", textAlign: "center",
        }}>
          {rfcFinal
            ? <ClaveSeparada clave={rfcFinal} segmentos={SEGMENTOS_RFC} />
            : <span style={{ color: "#999" }}>Para calcularlo falta: {faltan.join(", ")}</span>}
        </div>

        {coincide && (
          <Alert type="success" showIcon style={{ marginBottom: 10 }}
            message={rfcCapturado === rfc
              ? "Coincide con el RFC capturado."
              : "Coincide con el RFC capturado salvo la homoclave; se conserva la capturada."} />
        )}
        {!coincide && rfc && rfcCapturado && (
          <Alert type="warning" showIcon style={{ marginBottom: 10 }}
            message={`El RFC capturado (${rfcCapturado}) no corresponde a estos datos.`} />
        )}
        {!coincide && rfc && (
          <Alert type="info" showIcon style={{ marginBottom: 10 }}
            message="La homoclave se calcula con el algoritmo del SAT"
            description="A dos personas con el mismo nombre el SAT les puede asignar otra. Confírmalo con la constancia de situación fiscal del empleado o con la validación en SAT." />
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 0 10px" }}>
          <Space>
            <ButtonItem danger onClick={() => setVisible(false)}>Cancelar</ButtonItem>
            <ButtonItem type="primary" disabled={!rfcFinal} onClick={usar}>Usar este RFC</ButtonItem>
          </Space>
        </div>
      </ModalItem>
    </>
  );
};

export default CalculadoraRfc;
